import { FormGroup, FormLabel, Stack } from '@mui/material';
import CustomTextValidator from 'common/components/CustomTextValidators';
import { useAuthLicenseAddOns } from 'common/features/licenseFeatures/useAuthLicenseAddOns';
import { messageNotAuthorize } from 'common/utils/CONST';
import { useFormFields } from 'libs/hooksLib';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import CustomButton from 'common/components/CustomButton';
import CustomTooltip from 'common/components/CustomTooltip';
import useFlexBoxStyles from 'common/flexBox';
import { ValidatorForm } from 'react-material-ui-form-validator';
import EventFormActorRoleSelector from './EventFormActorRoleSelector';

const emailRegExp =
    /^$|^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const EventFormActorForm = (props) => {
    const { event, actors, onSubmit, guestLimit } = props;

    const { classes: flexBox, cx } = useFlexBoxStyles();

    const { t } = useTranslation();
    const isAuthorize = useAuthLicenseAddOns();

    const [fields, handleActorFieldChange, resetForm] = useFormFields({
        name: '',
        email: '',
        role: 'host',
    });

    useEffect(() => {
        if (actors.find((actor) => actor.role === 'host')) {
            handleActorFieldChange({
                target: {
                    name: 'role',
                    value: 'guest',
                },
            });
        } else {
            handleActorFieldChange({
                target: {
                    name: 'role',
                    value: 'host',
                },
            });
        }
    }, [actors, handleActorFieldChange]);

    const onLimit = () => {
        const guests = actors?.filter((actor) => actor?.role === 'guest') || [];
        return Boolean(guestLimit === guests.length);
    };

    useEffect(() => {
        ValidatorForm.addValidationRule('isValidEmailRegexp', (value) => {
            return Boolean(value.match(emailRegExp));
        });
        ValidatorForm.addValidationRule('emailAdmin', (value) => {
            return Boolean(actors.findIndex((act) => act.isAdmin && act.email === value) === -1);
        });
        ValidatorForm.addValidationRule('emailChatManager', (value) => {
            return Boolean(actors.findIndex((act) => act.role === 'chat_manager' && act.email === value) === -1);
        });
        return () => {
            ValidatorForm.removeValidationRule('isValidEmailRegexp');
            ValidatorForm.removeValidationRule('emailAdmin');
            ValidatorForm.removeValidationRule('emailChatManager');
        };
    }, [actors]);

    const validateRoleForm = () => {
        return (
            fields.email &&
            (event.hostProvider === 'web' || fields.role !== 'host' || fields.name) &&
            !actors.find((actor) => actor.email === fields.email)
        );
    };

    const addActor = (e) => {
        e.preventDefault();
        onSubmit(fields, () =>
            resetForm({
                name: '',
                email: '',
                role: actors.find((actor) => actor.role === 'host') || fields.role === 'host' ? 'guest' : 'host',
            }),
        );
    };

    const actorInput = (
        <FormGroup>
            <FormLabel>{t('event.eventFormActors.inviteGuest')}</FormLabel>

            <Stack direction="column" spacing={1}>
                {event.hostProvider !== 'web' && (
                    <CustomTextValidator
                        name="name"
                        fullWidth={true}
                        variant="outlined"
                        autoComplete="new-name"
                        value={fields.name}
                        onChange={handleActorFieldChange}
                        placeholder={t('event.eventFormActors.name')}
                    />
                )}

                <Stack direction="row" justifyContent="space-between" className={cx(flexBox.w100)}>
                    <div className={cx(flexBox.w75, flexBox.marginRight2)}>
                        <CustomTextValidator
                            fullWidth={true}
                            name="email"
                            value={fields.email}
                            onChange={handleActorFieldChange}
                            validators={['required', 'isEmail', 'emailAdmin', 'emailChatManager', 'isValidEmailRegexp']}
                            errorMessages={[
                                t('event.eventFormActors.inviteGuestRequired'),
                                t('event.eventFormActors.invalidEmail'),
                                t('event.eventFormActors.usedEmailAdmin'),
                                t('event.eventFormActors.usedEmailChatManager'),
                                t('event.eventFormActors.invalidEmail'),
                            ]}
                            placeholder={t('event.eventFormActors.addGuestPlaceholder')}
                            disabled={
                                fields.role !== 'host' && event.type === 'one_to_many' && !isAuthorize(['multi_guest'])
                            }
                        />
                    </div>
                    <div className={cx(flexBox.w25, flexBox.marginRight2)}>
                        <EventFormActorRoleSelector
                            onLimit={onLimit()}
                            actors={actors}
                            role={fields.role}
                            handleRoleChange={handleActorFieldChange}
                        />
                    </div>

                    <CustomButton
                        variant="contained"
                        color="primary"
                        disabled={
                            !validateRoleForm() ||
                            (fields.role !== 'host' && event.type === 'one_to_many' && !isAuthorize(['multi_guest'])) ||
                            (fields.role !== 'host' && event.record) ||
                            !fields.email.match(emailRegExp)
                        }
                        onClick={addActor}
                    >
                        <span>{t('event.eventFormActors.add')}</span>
                    </CustomButton>
                </Stack>
            </Stack>
        </FormGroup>
    );

    return (
        (!actors ||
            (!onLimit() && event.hostProvider === 'web' && !event.record) ||
            !actors.find((actor) => actor.role === 'host')) && (
            <>
                {fields.role !== 'host' && event.type === 'one_to_many' && !isAuthorize(['multi_guest']) ? (
                    <CustomTooltip arrow title={t(messageNotAuthorize)} placement={'left'}>
                        {actorInput}
                    </CustomTooltip>
                ) : (
                    actorInput
                )}
            </>
        )
    );
};

export default EventFormActorForm;
